import { describeNonRequestableRequests } from './nonRequestableFields';
import { hasCampaignMessageBody } from './campaignComposer';
import {
  CAMPAIGN_BODY_LIKE_VALEDICTION_PATTERN,
  CAMPAIGN_LAUNCH_GREETING_SOURCE,
  CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN,
  CAMPAIGN_SIGNATURE_MARKER_PATTERN,
  CAMPAIGN_VALEDICTION_LINE_PATTERN,
  looksLikeCampaignBusinessIdentity,
  looksLikeCampaignPersonIdentity,
  looksLikeCampaignSingleIdentity,
  looksLikeCampaignIdentityDetail,
  isCampaignGreetingOnlyMessage,
} from './campaignIdentityLines';

// Re-exported so existing importers keep reading these off draftGuards.
export { CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN } from './campaignIdentityLines';
export { stripTrailingCampaignSignature } from './campaignComposer';

const PLACEHOLDER_PATTERNS = [
  /\[[^\]\n]{1,80}\]/i,
  /\{\{[^}\n]+\}\}/,
  /<[^>\n]*(name|insert|response|placeholder)[^>\n]*>/i,
  /(?:\[\s*|\{\{?\s*|<\s*)[\p{L}_][^\r\n\]}>]{0,79}(?=$|\r?\n)/u,
  /［[^\n］]{1,80}］|【[^\n】]{1,80}】|〖[^\n〗]{1,80}〗|〘[^\n〙]{1,80}〙|〚[^\n〛]{1,80}〛|⟦[^\n⟧]{1,80}⟧|⟨[^\n⟩]{1,80}⟩|〈[^\n〉]{1,80}〉|《[^\n》]{1,80}》|「[^\n」]{1,80}」|『[^\n』]{1,80}』|〔[^\n〕]{1,80}〕|﹙[^\n﹚]{1,80}﹚|﹛[^\n﹜]{1,80}﹜|﹝[^\n﹞]{1,80}﹞|｛[^\n｝]{1,80}｝|＜[^\n＞]{1,80}＞|（[^\n）]{1,80}）/u,
  /(?:[%％][\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79}[%％]|[@＠][\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79}[@＠]|(?:__|＿＿)[\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79}(?:__|＿＿)|[$＄][\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79})/u,
  /(?:(?:\(\(|<<|##|~~|\|\||::)[\p{L}\p{M}_][\p{L}\p{M}\p{N}_ -]{0,79}(?:\)\)|>>|##|~~|\|\||::))/u
];

const QUOTE_OR_DISCLAIMER_PATTERNS = [
  /the contents of this e-?mail and its attachments are intended solely/i,
  /this e-?mail transmission may be confidential/i,
  /confidentiality notice/i,
  /this message and any attachments are confidential/i,
  /^From:\s.+/im,
  /^On\s+.+wrote:\s*$/im,
  /^-{2,}\s*Original Message\s*-{2,}$/im
];

const UNSAFE_CONTEXT_PHRASES = [
  /\bjust like before\b/i
];

const CAMPAIGN_LAUNCH_SCHEDULING_PATTERNS = [
  /\btour\s+scheduling\b/i,
  /\btour\s+is\s+being\s+scheduled\b/i,
  /\bbefore\s+we\s+proceed\s+with\s+tour\b/i,
  /\binclude\s+(?:it|the\s+space|this\s+space)\s+as\s+(?:a\s+)?tour\s+option\b/i,
  /\binclude\s+the\s+space\s+for\s+tours\b/i,
  /\bproceed\s+with\s+.*\bLOIs?\b/i,
  /\bLOIs?\b/i
];

// This is deliberately launch-only parity with the live server refusal. The
// lossless composer still preserves and exposes ambiguous user-authored text;
// the final campaign boundary refuses the same greeting-only shapes as the
// server without rewriting a byte.
const CAMPAIGN_MESSAGE_CONTENT_ERROR = 'Add message content after the greeting.';
const CAMPAIGN_GREETING_PREFIX_PATTERN = new RegExp(
  `^(?:${CAMPAIGN_LAUNCH_GREETING_SOURCE})(?=[\\s,!?:;.–—[{]|$)`,
  'iu'
);
const CAMPAIGN_ATTACHED_GREETING_RECIPIENT_PATTERN = new RegExp(
  `^(?:${CAMPAIGN_LAUNCH_GREETING_SOURCE})(?:-|/)(?:[\\p{Lu}][\\p{L}\\p{M}'’.-]{0,79})(?:[\\t ]+[\\p{Lu}][\\p{L}\\p{M}'’.-]{0,79}){0,5}[,!?:;](?=\\s|$)`,
  'u'
);
function startsWithCampaignGreeting(value) {
  const text = String(value || '').trimStart();
  if (CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN.test(text)) return false;
  return CAMPAIGN_GREETING_PREFIX_PATTERN.test(text)
    || CAMPAIGN_ATTACHED_GREETING_RECIPIENT_PATTERN.test(text);
}

function hasUnexpectedCampaignGreeting(value) {
  const sentenceLines = String(value || '')
    .replace(/\r\n?/gu, '\n')
    .replace(/([.!?])\s+/gu, '$1\n')
    .split('\n');
  let sawContent = false;
  for (const line of sentenceLines) {
    const segment = line.trim();
    if (!segment) continue;
    if (startsWithCampaignGreeting(segment) && sawContent) return true;
    sawContent = true;
  }
  return false;
}

function readCampaignContentBlock(lines, startIndex) {
  let index = startIndex;
  while (index < lines.length && !lines[index].trim()) index += 1;
  const block = [];
  while (index < lines.length && lines[index].trim() && block.length < 6) {
    block.push(lines[index].trim());
    index += 1;
  }
  return block;
}

function hasCampaignSignatureMaterial(value, bodyFieldLabels = []) {
  const text = String(value || '');
  if (CAMPAIGN_SIGNATURE_MARKER_PATTERN.test(text)) return true;
  const lines = text.replace(/\r\n?/gu, '\n').split('\n');
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (!line) continue;
    if (CAMPAIGN_VALEDICTION_LINE_PATTERN.test(line)) {
      const identityBlock = readCampaignContentBlock(lines, index + 1);
      const firstLine = identityBlock[0] || '';
      const firstLineIsPerson = looksLikeCampaignPersonIdentity(firstLine, bodyFieldLabels)
        || looksLikeCampaignSingleIdentity(firstLine, bodyFieldLabels);
      if (
        (
          firstLineIsPerson
          || (
            !CAMPAIGN_BODY_LIKE_VALEDICTION_PATTERN.test(line)
            && looksLikeCampaignBusinessIdentity(firstLine, bodyFieldLabels)
          )
        )
        && identityBlock.slice(1).every(candidate => (
          looksLikeCampaignPersonIdentity(candidate, bodyFieldLabels)
          || looksLikeCampaignSingleIdentity(candidate, bodyFieldLabels)
          || looksLikeCampaignIdentityDetail(candidate)
        ))
      ) return true;
    }
    if (looksLikeCampaignPersonIdentity(line, bodyFieldLabels)) {
      const identityBlock = readCampaignContentBlock(lines, index);
      if (
        identityBlock.length > 1
        && identityBlock.slice(1).every(looksLikeCampaignIdentityDetail)
        && identityBlock.slice(1).some(looksLikeCampaignIdentityDetail)
      ) return true;
    }
  }
  return false;
}

const CAMPAIGN_REQUEST_FIELD_ALIASES = {
  total_sf: ['total sf', 'available sf', 'square footage'],
  rent: ['rent sf yr', 'rent per sf', 'asking rent', 'rental rate', 'lease rate'],
  op_ex: ['ops ex', 'op ex', 'opex', 'operating expenses', 'nnn expenses', 'nnn charges'],
  drive_ins: ['drive ins', 'drive in doors', 'drive in door', 'ramp doors', 'ramp door'],
  docks: ['docks', 'loading docks', 'loading dock', 'dock doors', 'dock door'],
  ceiling_height: ['ceiling ht', 'ceiling height', 'clear height'],
  power: ['available power', 'power details', 'power requirements', 'power capacity', 'electrical service', 'electrical capacity', 'amperage', 'voltage'],
  year_built: ['year built', 'construction year'],
  flyer: ['flyer', 'marketing package', 'brochure', 'floor plan', 'floorplan'],
};

const CAMPAIGN_REQUEST_FIELD_LABEL_ALIASES = {
  power: ['power'],
};

const normalizeScopeText = value => String(value || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .split(' ')
  .map(token => ({
    street: 'st', road: 'rd', avenue: 'ave', boulevard: 'blvd', highway: 'hwy',
    drive: 'dr', lane: 'ln', court: 'ct', parkway: 'pkwy', suite: 'ste',
    north: 'n', south: 's', east: 'e', west: 'w',
  }[token] || token))
  .join(' ');

function containsNormalizedPhrase(text, phrase) {
  return ` ${text} `.includes(` ${normalizeScopeText(phrase)} `);
}

function scriptRequestsField(text, normalizedText, group, aliases) {
  if (aliases.some(alias => containsNormalizedPhrase(normalizedText, alias))) return true;
  if (group !== 'power') return false;

  return /(?:^|\n)\s*[-*•]\s*(?:available\s+)?power\b/i.test(text)
    || /\b(?:send|share|provide|confirm|include|need|request)\b\s+(?:(?:the|available|property|electrical)\s+){0,3}power\b/i.test(text);
}

function findUnexpectedCampaignRequests(text, askFields) {
  if (!Array.isArray(askFields)) return [];

  const normalizedText = normalizeScopeText(text);
  const normalizedAskFields = askFields.map(normalizeScopeText).filter(Boolean);
  const allowedGroups = new Set(
    Object.entries(CAMPAIGN_REQUEST_FIELD_ALIASES)
      .filter(([group, aliases]) => normalizedAskFields.some(field => (
        [...aliases, ...(CAMPAIGN_REQUEST_FIELD_LABEL_ALIASES[group] || [])]
          .some(alias => containsNormalizedPhrase(field, alias))
      )))
      .map(([group]) => group)
  );

  return Object.entries(CAMPAIGN_REQUEST_FIELD_ALIASES)
    .filter(([group, aliases]) => (
      !allowedGroups.has(group)
      && scriptRequestsField(text, normalizedText, group, aliases)
    ))
    .map(([group]) => group);
}

function hasLocalPartGreeting(text) {
  const firstLine = text.split('\n').map(line => line.trim()).find(Boolean) || '';
  const match = firstLine.match(/^hi\s+([^,\n]+),/i);
  if (!match) return false;

  const greetingName = match[1].trim().toLowerCase();
  if (greetingName.includes('@')) return true;
  return /[._%+-]/.test(greetingName) || /\d/.test(greetingName);
}

export function validateOutboundDraft(body, { bodyFieldLabels = [] } = {}) {
  const hasStringBody = typeof body === 'string';
  const text = hasStringBody ? body.trim() : '';
  const errors = [];

  if (!hasStringBody) {
    errors.push('Message body is malformed.');
  } else if (!text) {
    errors.push('Message body is empty.');
  } else if (!hasCampaignMessageBody(text, { bodyFieldLabels })) {
    errors.push('Add message content after the greeting.');
  }

  if (PLACEHOLDER_PATTERNS.some(pattern => pattern.test(text))) {
    errors.push('Resolve draft placeholders before sending.');
  }

  if (hasLocalPartGreeting(text)) {
    errors.push('Replace email/local-part greeting with a real name or neutral greeting.');
  }

  const meaningfulLines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  if (meaningfulLines.some(line => /^[-*]\s*$/.test(line))) {
    errors.push('Remove blank bullet points before sending.');
  }

  if (QUOTE_OR_DISCLAIMER_PATTERNS.some(pattern => pattern.test(text))) {
    errors.push('Remove quoted history or disclaimer text before sending.');
  }

  if (UNSAFE_CONTEXT_PHRASES.some(pattern => pattern.test(text))) {
    errors.push('Remove context-dependent phrases before sending.');
  }

  return {
    ok: errors.length === 0,
    errors
  };
}

export function validateCampaignLaunchDraft(
  body,
  { forbiddenValues = [], askFields = null, nonRequestableFields = [] } = {}
) {
  const base = validateOutboundDraft(body, { bodyFieldLabels: askFields || [] });
  const text = String(body || '');
  const errors = [...base.errors];

  if (
    isCampaignGreetingOnlyMessage(text)
    && !errors.includes(CAMPAIGN_MESSAGE_CONTENT_ERROR)
  ) {
    errors.push(CAMPAIGN_MESSAGE_CONTENT_ERROR);
  }

  if (CAMPAIGN_LAUNCH_SCHEDULING_PATTERNS.some(pattern => pattern.test(text))) {
    errors.push('Remove tour scheduling or LOI language from campaign launch copy.');
  }

  if (hasUnexpectedCampaignGreeting(text)) {
    errors.push('Remove the extra greeting from campaign launch copy.');
  }

  if (hasCampaignSignatureMaterial(text, askFields || [])) {
    errors.push('Remove embedded signature or sender identity from campaign launch copy.');
  }

  const normalizedText = normalizeScopeText(text);
  const matchedForbiddenScopeValue = forbiddenValues
    .filter(value => typeof value === 'string' && value.trim().length > 0)
    .find(value => ` ${normalizedText} `.includes(` ${normalizeScopeText(value)} `));

  if (matchedForbiddenScopeValue) {
    errors.push('Remove the internal campaign label or another property from this property-specific message.');
  }

  // A column the advisor marked Note, Skip or formula is refused on its own
  // terms, whether or not an Ask contract was supplied: an unknown Ask set is
  // not permission to ask for a column that was explicitly marked never-ask.
  // The message IS the guidance - it names the column, its role and the fix.
  describeNonRequestableRequests(text, nonRequestableFields).forEach(guidance => {
    if (!errors.includes(guidance)) errors.push(guidance);
  });

  const unexpectedRequests = findUnexpectedCampaignRequests(text, askFields);
  unexpectedRequests.forEach(unexpectedRequest => {
    errors.push(`Remove the ${unexpectedRequest} request because it is not configured as Ask.`);
  });

  return {
    ok: errors.length === 0,
    errors,
    scopeMatches: matchedForbiddenScopeValue ? [{ value: matchedForbiddenScopeValue }] : [],
    requestMatches: unexpectedRequests.map(field => ({ field })),
  };
}
